import { z } from 'zod';

// `endpoint` is the AP client (`/api/v2/{client}/`). The bank-feed service sits
// behind its own prefix (`/api/bankfeed/v1/`), so its calls go through
// `bankFeedEndpoint`. The `bank-feed/company/permissions` endpoints are NOT part
// of that service — they stay on AP.
import { endpoint } from '@/services/api/endpoint';
import { bankFeedApi as bankFeedClient } from '@/services/api/instances';

const bankFeedEndpoint = bankFeedClient.endpoint;

import {
  BankFeedConnectionAccountSchema,
  BankFeedConnectionSchema,
  BankFeedExportPathSchema,
  BankFeedExportStatusResponseSchema,
  BankFeedSummarySchema,
  BankFeedSyncSettingsSchema,
  BankFeedTransactionsExportBodySchema,
  BankFeedTransactionsExportResponseSchema,
  BankProvidersResponseSchema,
  ConnectionCreateBodySchema,
  ConnectionCreateResponseSchema,
  ConnectionDelegationBodySchema,
  ConnectionDelegationResponseSchema,
  ConnectionIdPathSchema,
  ConnectionsByIdsBodySchema,
  ConnectionsByIdsResponseSchema,
  ConnectionsGridResponseSchema,
  ConnectionsLimitResponseSchema,
  ConnectionUpdateBodySchema,
  ErpAccountsMatchBodySchema,
  ErpAccountsResponseSchema,
  SupportTicketBodySchema,
  TransactionsAutocompleteBodySchema,
  TransactionsAutocompleteResponseSchema,
  TransactionsGridHeadersQuerySchema,
  TransactionsGridHeadersResponseSchema,
  TransactionsGridSearchBodySchema,
  BankFeedTransactionsGridResponseSchema,
  UserPermissionsGridResponseSchema,
  UserPermissionsUpdateBodySchema,
  UserPermissionsUpdateResponseSchema,
} from '@/domain/bankFeed/schemas';

export const bankFeedApi = {
  // ── reads ───────────────────────────────────────────────────────────────
  getConnections: bankFeedEndpoint({
    method: 'GET',
    url: 'connections/grid',
    response: ConnectionsGridResponseSchema,
  }),

  getConnectionsLimit: bankFeedEndpoint({
    method: 'GET',
    url: 'connections/limit',
    response: ConnectionsLimitResponseSchema,
  }),

  getProviders: bankFeedEndpoint({
    method: 'GET',
    url: 'providers',
    response: BankProvidersResponseSchema,
  }),

  getErpAccounts: bankFeedEndpoint({
    method: 'GET',
    url: 'erp-accounts',
    response: ErpAccountsResponseSchema,
  }),

  getUserPermissions: endpoint({
    method: 'GET',
    url: 'bank-feed/company/permissions',
    response: UserPermissionsGridResponseSchema,
  }),

  getSyncSchedules: bankFeedEndpoint({
    method: 'GET',
    url: 'preferences',
    response: BankFeedSyncSettingsSchema,
  }),

  getConnectionsByIds: bankFeedEndpoint({
    method: 'POST',
    url: 'connections/grid/search',
    body: ConnectionsByIdsBodySchema,
    response: ConnectionsByIdsResponseSchema,
  }),

  getTransactionsAutocomplete: bankFeedEndpoint({
    method: 'POST',
    url: 'transactions/grid/autocomplete',
    body: TransactionsAutocompleteBodySchema,
    response: TransactionsAutocompleteResponseSchema,
  }),

  getTransactionsGridHeaders: bankFeedEndpoint({
    method: 'GET',
    url: 'transactions/grid/headers',
    query: TransactionsGridHeadersQuerySchema,
    response: TransactionsGridHeadersResponseSchema,
  }),

  getTransactionsGridSummary: bankFeedEndpoint({
    method: 'GET',
    url: 'transactions/grid/summary',
    response: BankFeedSummarySchema,
  }),

  getTransactionsGridSearch: bankFeedEndpoint({
    method: 'POST',
    url: 'transactions/grid/search',
    body: TransactionsGridSearchBodySchema,
    response: BankFeedTransactionsGridResponseSchema,
  }),

  getExport: bankFeedEndpoint({
    method: 'GET',
    url: (p: { exportId: string }) => `exports/${p.exportId}`,
    path: BankFeedExportPathSchema,
    response: BankFeedExportStatusResponseSchema,
  }),

  // ── writes ──────────────────────────────────────────────────────────────
  createConnection: bankFeedEndpoint({
    method: 'POST',
    url: 'connections',
    body: ConnectionCreateBodySchema,
    response: ConnectionCreateResponseSchema,
  }),

  // DPM-670: one write creates the connection and sends the authorization
  // request to the signatory (backend-confirmed contract). Also reused by
  // "Resend authorization link".
  delegateConnection: bankFeedEndpoint({
    method: 'POST',
    url: 'connections/delegate',
    body: ConnectionDelegationBodySchema,
    response: ConnectionDelegationResponseSchema,
  }),

  updateConnection: bankFeedEndpoint({
    method: 'PATCH',
    url: (p: { id: string }) => `connections/${p.id}`,
    path: ConnectionIdPathSchema,
    body: ConnectionUpdateBodySchema,
    response: BankFeedConnectionSchema,
  }),

  deleteConnection: bankFeedEndpoint({
    method: 'DELETE',
    url: (p: { id: string }) => `connections/${p.id}`,
    path: ConnectionIdPathSchema,
    response: z.unknown(),
  }),

  // AP endpoint (not the bank-feed service): opens a Freshdesk ticket.
  createSupportTicket: endpoint({
    method: 'POST',
    url: 'createTicket',
    body: SupportTicketBodySchema,
    response: z.unknown(),
  }),

  syncTransactions: bankFeedEndpoint({
    method: 'POST',
    url: (p: { id: string }) => `connections/${p.id}/transactions/sync`,
    path: ConnectionIdPathSchema,
    response: BankFeedConnectionSchema,
  }),

  matchErpAccount: bankFeedEndpoint({
    method: 'POST',
    url: 'erp-accounts/match',
    body: ErpAccountsMatchBodySchema,
    response: BankFeedConnectionAccountSchema,
  }),

  updateUserPermissions: endpoint({
    method: 'POST',
    url: 'bank-feed/company/permissions',
    body: UserPermissionsUpdateBodySchema,
    response: UserPermissionsUpdateResponseSchema,
  }),

  updateSyncSchedules: bankFeedEndpoint({
    method: 'POST',
    url: 'preferences',
    body: BankFeedSyncSettingsSchema,
    response: BankFeedSyncSettingsSchema,
  }),

  triggerTransactionsExport: bankFeedEndpoint({
    method: 'POST',
    url: 'transactions/grid/export',
    body: BankFeedTransactionsExportBodySchema,
    response: BankFeedTransactionsExportResponseSchema,
  }),
};
